// #region Imports
import { PayloadAction, SerializedError, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import EntityMemberAPIService from "_shared/entity-members/_api/entity-member-api-service";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Session from "users/__session/session";
// #endregion

// #region Store State Type
export type IndividualStoreState = {
  vendors: {
    entries?: IndividualJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  byEntityId: {
    entries: Record<string, IndividualJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byUserId: {
    entries: Record<string, IndividualJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byId: {
    entries: Record<string, IndividualJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  avatars: {
    entries: Record<string, string>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};
// #endregion

// #region Initial State
const initialState: IndividualStoreState = {
  vendors: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byEntityId: {
    entries: {},
    loading: {},
    error: {},
  },
  byUserId: {
    entries: {},
    loading: {},
    error: {},
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  avatars: {
    entries: {},
    loading: {},
    error: {},
  },
};
// #endregion

function isUserMissingFromStore(individuals: IndividualStoreState, userId: Guid): boolean {
  return (
    !individuals.byUserId.entries[userId.value] &&
    !individuals.byUserId.loading[userId.value] &&
    !individuals.byUserId.error[userId.value]
  );
}

// #region Async Thunks
export const getContactIndividualForum = createAsyncThunk<
  ForumJSON,
  { individualId: Guid | null },
  { rejectValue: unknown }
>(
  "individuals/contactIndividual",
  async ({ individualId }: { individualId: Guid | null }, thunkAPI) => {
    try {
      if (!individualId) throw new Error("Individual ID is required to contact individual.");
      return await thunkAPI.dispatch(
        individualsApi.endpoints.contactIndividual.initiate({ individualId: individualId.value }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateIndividualVendors = createAsyncThunk<IndividualJSON[], void>(
  "individuals/populateIndividualVendors",
  async (_arg, thunkAPI) => {
    try {
      return await thunkAPI.dispatch(individualsApi.endpoints.getIndividualVendors.initiate()).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateIndividualsByUserIds = createAsyncThunk<IndividualJSON[], { userIds: Guid[] }>(
  "individuals/getIndividualsByUserIds",
  async ({ userIds }: { userIds: Guid[] }, thunkAPI) => {
    if (!userIds.length) return [];

    const store: RootState = thunkAPI.getState() as RootState;
    const missingIndividualUserIds = _.uniqWith(
      userIds.filter((userId) => isUserMissingFromStore(store.individuals, userId)),
      (a, b) => a.isEqualTo(b),
    );
    if (!missingIndividualUserIds.length) return [];

    try {
      return await thunkAPI.dispatch(
        individualsApi.endpoints.getIndividualsByUserIds.initiate({
          userIds: new Set(missingIndividualUserIds.map((id) => id.value)),
        }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: ({ userIds }, { getState }) => {
      const state: RootState = getState() as RootState;
      const allUsersExistInStore = userIds.every((userId) => !!state.individuals.byUserId.entries[userId.value]);
      const everyUserIsLoading = userIds.every((userId) => state.individuals.byUserId.loading[userId.value]);
      const everyUserIsErrored = userIds.every((userId) => !!state.individuals.byUserId.error[userId.value]);
      if (allUsersExistInStore || everyUserIsLoading || everyUserIsErrored) {
        return false;
      }
    },
  },
);

export const populateIndividualById = createAsyncThunk(
  "individuals/getIndividualById",
  async ({ individualId }: { individualId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI.dispatch(
        individualsApi.endpoints.getIndividualById.initiate({ individualId: individualId.value }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: ({ individualId }, { getState }) => {
      const state: RootState = getState() as RootState;
      if (state.individuals.byId.entries[individualId.value]) {
        return false;
      }
    },
  },
);

export const populateIndividualsByIds = createAsyncThunk<Individual[], { ids: Guid[] }>(
  "individuals/populateByIds",
  async ({ ids }: { ids: Guid[] }, thunkAPI) => {
    if (!ids.length) return [];

    const store: RootState = thunkAPI.getState() as RootState;
    const missingIndividualIds = _.uniqWith(
      ids.filter((id) => !store.individuals.byId.entries[id.value]),
      (a, b) => a.isEqualTo(b),
    );

    if (!missingIndividualIds.length) return [];

    try {
      const result = await thunkAPI.dispatch(
        individualsApi.endpoints.getIndividualsByIds.initiate({ ids: missingIndividualIds.map((id) => id.value) }),
      ).unwrap();
      if (!result.length) return [];
      return Individual.fromJSONArray(result) ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateIndividualsByEntity = createAsyncThunk<
  IndividualJSON[] | undefined,
  { session: Session; legalEntityId?: Guid }
>(
  "individuals/getIndividualsForEntityMembers",
  async ({ session, legalEntityId }: { session: Session; legalEntityId?: Guid }, thunkAPI) => {
    if (!legalEntityId) return;
    const abortController = new AbortController();
    const apiService = new EntityMemberAPIService({ session, abortController });

    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const entityMembersResponse = await apiService.getMembersForLegalEntity(legalEntityId);
      const individualUserIds = entityMembersResponse.map((memberJson) => Guid.fromJSON(memberJson.userId));
      const individualsResponse: IndividualJSON[] = [];
      const missingIndividualUserIds: Guid[] = [];
      const store: RootState = thunkAPI.getState() as RootState;
      individualUserIds.forEach((userId) => {
        if (!userId) return;
        const individualJson = store.individuals.byUserId.entries[userId.value];
        if (individualJson) individualsResponse.push(individualJson);
        else missingIndividualUserIds.push(userId);
      });

      if (!missingIndividualUserIds.length) return individualsResponse;
      const returnedIndividuals = await thunkAPI.dispatch(
        individualsApi.endpoints.getIndividualsByUserIds.initiate({
          userIds: new Set(missingIndividualUserIds.map((id) => id.value)),
        }),
      ).unwrap();
      returnedIndividuals.forEach((individualJson) => individualsResponse.push(individualJson));
      return individualsResponse;
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);

export const populateIndividualAvatar = createAsyncThunk(
  "individuals/getIndividualAvatar",
  async (
    {
      individualId,
    }: {
      individualId: Guid | null;
    },
    thunkAPI,
  ) => {
    try {
      if (!individualId) return;
      return await thunkAPI.dispatch(
        individualsApi.endpoints.getIndividualAvatar.initiate({ individualId: individualId.value }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateIndividual = createAsyncThunk(
  "individuals/updateIndividual",
  async (
    {
      individualId,
      isVisible,
      originalProfile,
      updatedProfile,
    }: {
      individualId: Guid;
      isVisible: boolean;
      originalProfile?: IndividualProfile;
      updatedProfile?: IndividualProfile;
    },
    thunkAPI,
  ) => {
    try {
      return await thunkAPI.dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individualId.value,
          isVisible,
          originalProfile,
          updatedProfile,
        }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createIndividualAvatar = createAsyncThunk(
  "individuals/createIndividualAvatar",
  async ({ individualId, avatar }: { individualId: Guid; avatar: File }, thunkAPI) => {
    try {
      await thunkAPI.dispatch(
        individualsApi.endpoints.createIndividualAvatar.initiate({ individualId: individualId.value, file: avatar }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createIndividualResume = createAsyncThunk(
  "individuals/createIndividualResume",
  async ({ individualId, resume }: { individualId: Guid; resume: File }, thunkAPI) => {
    try {
      await thunkAPI.dispatch(
        individualsApi.endpoints.createIndividualResume.initiate({ individualId: individualId.value, file: resume }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const downloadIndividualResume = createAsyncThunk(
  "individuals/downloadIndividualResume",
  async ({ individualId }: { individualId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI.dispatch(
        individualsApi.endpoints.downloadIndividualResume.initiate({ individualId: individualId.value }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const deleteIndividualResume = createAsyncThunk(
  "individuals/deleteIndividualResume",
  async ({ individualId }: { individualId: Guid }, thunkAPI) => {
    try {
      await thunkAPI.dispatch(
        individualsApi.endpoints.deleteIndividualResume.initiate({ individualId: individualId.value }),
      ).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
// #endregion

// #region Slice
export const individualsSlice = createSlice({
  name: "individuals",
  initialState,
  // #region Simple Reducers
  reducers: {
    addIndividual(state, action) {
      if (!state.byUserId.entries[action.payload.userId.value]) {
        state.byUserId.entries[action.payload.userId.value] = action.payload;
      }
    },
    addIndividualJSON: (state, action: PayloadAction<IndividualJSON>) => {
      const individualJSON = action.payload;
      if (!individualJSON.id || !individualJSON.userId) return;

      state.byUserId.entries[individualJSON.userId] = individualJSON;
      state.byId.entries[individualJSON.id] = individualJSON;
    },
    addAvatar: (state, action: PayloadAction<{ individualId: Guid; avatar: string }>) => {
      state.avatars.entries[action.payload.individualId.value] = action.payload.avatar;
    },
    removeAvatar: (state, action: PayloadAction<Guid>) => {
      delete state.avatars.entries[action.payload.value];
    },
  },
  // #endregion
  // #region Async Reducers
  extraReducers: (builder) => {
    // populateIndividualsByUserIds
    builder.addCase(populateIndividualsByUserIds.pending, (state, action) => {
      const userIds = action.meta.arg.userIds;
      if (!userIds.length) {
        return;
      }
      for (const userId of userIds) {
        if (!isUserMissingFromStore(state, userId)) {
          continue;
        }
        state.byUserId.loading[userId.value] = true;
        state.byUserId.error[userId.value] = null;
      }
    });
    builder.addCase(populateIndividualsByUserIds.fulfilled, (state, action) => {
      const userIds = action.meta.arg.userIds;
      if (!userIds.length) return;
      for (const individual of action.payload) {
        state.byUserId.entries[individual.userId] = individual;
      }
      for (const userId of userIds) {
        const key = userId.value;
        state.byUserId.loading[key] = false;
      }
    });
    builder.addCase(populateIndividualsByUserIds.rejected, (state, action) => {
      const userIds = action.meta.arg.userIds;
      if (!userIds.length) {
        return;
      }
      for (const userId of userIds) {
        state.byUserId.loading[userId.value] = false;
        state.byUserId.error[userId.value] = action.error;
      }
    });

    // populateIndividualsByEntity
    builder.addCase(populateIndividualsByEntity.pending, (state, action) => {
      const legalEntityId = action.meta.arg.legalEntityId;
      if (!legalEntityId) return;
      state.byEntityId.loading[legalEntityId.value] = true;
      state.byEntityId.error[legalEntityId.value] = null;
    });
    builder.addCase(populateIndividualsByEntity.fulfilled, (state, action) => {
      const legalEntityId = action.meta.arg.legalEntityId;
      if (!legalEntityId) return;
      action.payload?.forEach((individualJson) => {
        state.byUserId.entries[individualJson.userId] = individualJson;
      });
      state.byEntityId.loading[legalEntityId.value] = false;
      state.byEntityId.entries[legalEntityId.value] = action.payload ?? [];
    });
    builder.addCase(populateIndividualsByEntity.rejected, (state, action) => {
      const legalEntityId = action.meta.arg.legalEntityId;
      if (!legalEntityId) return;
      state.byEntityId.loading[legalEntityId.value] = false;
      state.byEntityId.error[legalEntityId.value] = action.error;
    });

    // getIndividualVendors
    builder.addMatcher(individualsApi.endpoints.getIndividualVendors.matchPending, (state) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
      state.vendors.loading = false;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualVendors.matchRejected, (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.error;
    });

    // getIndividualById
    builder.addMatcher(individualsApi.endpoints.getIndividualById.matchPending, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.byId.loading[individualId] = true;
      state.byId.error[individualId] = null;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualById.matchFulfilled, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.byId.loading[individualId] = false;
      if (!action.payload) {
        console.error("Individual not received by store.");
        return;
      }
      state.byId.entries[individualId] = action.payload;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualById.matchRejected, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.byId.loading[individualId] = false;
      state.byId.error[individualId] = action.error;
    });

    // getIndividualsByIds
    builder.addMatcher(individualsApi.endpoints.getIndividualsByIds.matchPending, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id] = true;
        state.byId.error[id] = null;
      }
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualsByIds.matchFulfilled, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const individual of action.payload) {
        state.byId.entries[individual.id] = individual;
      }
      for (const id of ids) {
        state.byId.loading[id] = false;
      }
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualsByIds.matchRejected, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id] = false;
        state.byId.error[id] = action.error;
      }
    });

    // getIndividualsByUserIds
    builder.addMatcher(individualsApi.endpoints.getIndividualsByUserIds.matchFulfilled, (state, action) => {
      if (!action.meta.arg.originalArgs) return;
      const individualsJSON: IndividualJSON[] = action.payload;

      for (const individualJSON of individualsJSON) {
        if (!individualJSON.id) {
          throw new Error("Individual data did not include id.");
        }
        state.byUserId.entries[individualJSON.userId] = individualJSON;
        state.byId.entries[individualJSON.id] = individualJSON;
      }
    });

    // getIndividualAvatar
    builder.addMatcher(individualsApi.endpoints.getIndividualAvatar.matchPending, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      if (!individualId) return;
      state.avatars.loading[individualId] = true;
      state.avatars.error[individualId] = null;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualAvatar.matchFulfilled, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      if (!individualId) return;
      state.avatars.entries[individualId] = action.payload ?? "";
      state.avatars.loading[individualId] = false;
    });
    builder.addMatcher(individualsApi.endpoints.getIndividualAvatar.matchRejected, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      if (!individualId) return;
      state.avatars.loading[individualId] = false;
      state.avatars.error[individualId] = action.error;
    });

    // updateIndividual
    builder.addMatcher(individualsApi.endpoints.updateIndividual.matchPending, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.byId.loading[individualId] = true;
      state.byId.error[individualId] = null;
    });
    builder.addMatcher(individualsApi.endpoints.updateIndividual.matchFulfilled, (state, action) => {
      const individualId = action.payload.id;
      state.byId.loading[individualId] = false;
      state.byId.entries[individualId] = action.payload;
      state.vendors.entries = undefined;
    });
    builder.addMatcher(individualsApi.endpoints.updateIndividual.matchRejected, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.byId.loading[individualId] = false;
      state.byId.error[individualId] = action.error;
    });

    // createIndividualAvatar
    builder.addMatcher(individualsApi.endpoints.createIndividualAvatar.matchPending, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.avatars.loading[individualId] = true;
      state.avatars.error[individualId] = null;
    });
    builder.addMatcher(individualsApi.endpoints.createIndividualAvatar.matchFulfilled, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.avatars.loading[individualId] = false;
      state.avatars.entries[individualId] = URL.createObjectURL(action.meta.arg.originalArgs.file);
    });
    builder.addMatcher(individualsApi.endpoints.createIndividualAvatar.matchRejected, (state, action) => {
      const individualId = action.meta.arg.originalArgs.individualId;
      state.avatars.loading[individualId] = false;
      state.avatars.entries[individualId] = "";
      state.avatars.error[individualId] = action.error;
    });
  },
  // #endregion
});
// #endregion

// #region Exports

export const { addIndividual, addIndividualJSON } = individualsSlice.actions;

// #region Individual By Id

// getIndividualById ----------------------------------------------------------
const selectIndividualById = createSelector(
  [(state: RootState) => state.individuals.byId.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, individualIdValue) => {
    if (!individualIdValue) return;
    const individualJson = entries[individualIdValue];
    if (!individualJson) return undefined;
    return Individual.fromJSON(individualJson);
  },
);
export const getIndividualById = (id?: Guid) => (state: RootState) => selectIndividualById(state, id);
// ----------------------------------------------------------------------------

export const getIsLoadingIndividualById = (id?: Guid) => (state: RootState) =>
  id ? state.individuals.byId.loading[id.value] : false;

export const getErrorLoadingIndividualById = (id?: Guid) => (state: RootState) =>
  id ? state.individuals.byId.error[id.value] : undefined;

// #endregion

// #region Individuals By Ids

// getIndividualsByIds --------------------------------------------------------
const selectIndividualsByIds = createSelector(
  [(state: RootState) => state.individuals.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) => {
    return ids
      .map((id) => {
        const individualJson = entries[id.value];
        if (!individualJson) return undefined;
        return Individual.fromJSON(individualJson);
      })
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByIds = (ids: Guid[]) => (state: RootState) => selectIndividualsByIds(state, ids);

// getLoadingIndividualIds ---------------------------------------------------
const selectLoadingIndividualIds = createSelector(
  [(state: RootState) => state.individuals.byId.loading],
  (loadingMap) => {
    return Object.keys(loadingMap)
      .filter((id) => loadingMap[id])
      .map((id) => new Guid(id))
      .filter((id) => id != undefined);
  },
);
export const getLoadingIndividualIds = () => (state: RootState) => selectLoadingIndividualIds(state);
// ----------------------------------------------------------------------------

// getErrorLoadingIndividualIds -----------------------------------------------
const selectErrorLoadingIndividualIds = createSelector(
  [(state: RootState) => state.individuals.byId.error, (_: RootState, ids: Guid[]) => ids],
  (errors, ids) => {
    return ids.reduce<Record<string, SerializedError | null>>((acc, id) => {
      acc[id.value] = errors[id.value] ?? null;
      return acc;
    }, {});
  },
);
export const getErrorLoadingIndividualIds = (ids: Guid[]) => (state: RootState) =>
  selectErrorLoadingIndividualIds(state, ids);
// ----------------------------------------------------------------------------

// #endregion

// #region Individuals By UserId

// getIndividualByUserId ------------------------------------------------------
const selectIndividualByUserId = createSelector(
  [(state: RootState) => state.individuals.byUserId.entries, (_: RootState, userId?: Guid) => userId?.value],
  (entries, userId) => {
    if (!userId) return;
    const individualJson = entries[userId];
    if (!individualJson) return;
    return Individual.fromJSON(individualJson);
  },
);
export const getIndividualByUserId = (userId?: Guid) => (state: RootState) => selectIndividualByUserId(state, userId);
// ----------------------------------------------------------------------------

export const getErrorLoadingIndividualByUserId = (userId?: Guid) => (state: RootState) =>
  userId ? state.individuals.byUserId.error[userId.value] : undefined;

// #endregion

// #region Individual By UserIds

// getIndividualsByUserIds ----------------------------------------------------
const selectIndividualsByUserIds = createSelector(
  [(state: RootState) => state.individuals.byUserId.entries, (_: RootState, userIds: Guid[] | Set<string>) => userIds],
  (entries, userIds) => {
    const idsArray = Array.isArray(userIds) ? userIds : Array.from(userIds).map((id) => new Guid(id));
    return idsArray
      .map((userId) => {
        const individualJson = entries[userId.value];
        if (!individualJson) return undefined;
        return Individual.fromJSON(individualJson);
      })
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByUserIds = (userIds: Guid[] | Set<string>) => (state: RootState) =>
  selectIndividualsByUserIds(state, userIds);
// ----------------------------------------------------------------------------

// getLoadingIndividualUserIds ------------------------------------------------
const selectLoadingIndividualUserIds = createSelector(
  [(state: RootState) => state.individuals.byUserId.loading],
  (loadingState) => {
    return Object.keys(loadingState)
      .filter((userId) => loadingState[userId])
      .map((userId) => new Guid(userId))
      .filter((userId) => userId != undefined);
  },
);
export const getLoadingIndividualUserIds = () => (state: RootState) => selectLoadingIndividualUserIds(state);
// ----------------------------------------------------------------------------

// getErrorsLoadingIndividualsByUserIds ---------------------------------------
const selectErrorsLoadingIndividualsByUserIds = createSelector(
  [(state: RootState) => state.individuals.byUserId.error, (_: RootState, userIds: Guid[]) => userIds],
  (errors, userIds) => {
    return userIds.reduce<Record<string, SerializedError | null>>((acc, userId) => {
      acc[userId.value] = errors[userId.value] ?? null;
      return acc;
    }, {});
  },
);
export const getErrorsLoadingIndividualsByUserIds = (userIds: Guid[]) => (state: RootState) =>
  selectErrorsLoadingIndividualsByUserIds(state, userIds);
// ----------------------------------------------------------------------------

// getAreErrorsLoadingIndividualsByUserIds ------------------------------------
const selectAreErrorsLoadingIndividualsByUserIds = createSelector(
  [(state: RootState) => state.individuals.byUserId.error, (_: RootState, userIds: Guid[]) => userIds],
  (errors, userIds) => {
    return userIds.some((userId) => errors[userId.value]);
  },
);
export const getAreErrorsLoadingIndividualsByUserIds = (userIds: Guid[]) => (state: RootState) =>
  selectAreErrorsLoadingIndividualsByUserIds(state, userIds);
// ----------------------------------------------------------------------------

// #endregion

// #region Individuals By Entity

// getIndividualsByEntity -----------------------------------------------------
export const selectIndividualsByEntity = createSelector(
  [(state: RootState) => state.individuals.byEntityId.entries, (_: RootState, entityId?: Guid) => entityId?.value],
  (entries, entityId) => {
    if (!entityId) return;
    const individualJsonArray = entries[entityId];
    if (!individualJsonArray) return;
    return individualJsonArray
      .map((individualJson) => Individual.fromJSON(individualJson))
      .filter((individual): individual is Individual => !!individual);
  },
);
export const getIndividualsByEntity = (entityId?: Guid) => (state: RootState) =>
  selectIndividualsByEntity(state, entityId);
// #endregion -----------------------------------------------------------------

export const getIndividualsIsLoadingByEntity = (entityId?: Guid) => (state: RootState) =>
  entityId ? state.individuals.byEntityId.loading[entityId.value] : false;
export const getErrorLoadingIndividualsByEntity = (entityId?: Guid) => (state: RootState) =>
  entityId ? state.individuals.byEntityId.error[entityId.value] : undefined;

// #endregion

// #region All Errors

// getAllIndividualErrors -----------------------------------------------------
const selectAllIndividualErrors = createSelector([(state: RootState) => state.individuals.byUserId.error], (errors) => {
  return Object.keys(errors)
    .filter((individualId) => individualId && errors[individualId])
    .map((individualId) => {
      const error = errors[individualId];
      if (!error) return null;
      const userId = new Guid(individualId);
      if (!userId) return null;
      return { userId, error };
    })
    .filter((record): record is IndividualError => !!record);
});
export const getAllIndividualErrors = () => (state: RootState) => selectAllIndividualErrors(state);
// ----------------------------------------------------------------------------

// #endregion

// #region Individual Vendors

// getIndividualVendors ----------------------------------------------------------
const selectIndividualVendors = createSelector([(state: RootState) => state.individuals.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Individual.fromJSON(json)).filter((c): c is Individual => !!c),
);
export const getIndividualVendors = () => (state: RootState) => selectIndividualVendors(state);
// ----------------------------------------------------------------------------

export const getIndividualVendorsIsLoading = () => (state: RootState) => state.individuals.vendors.loading;

export const getIndividualVendorsError = () => (state: RootState) => state.individuals.vendors.error;

// #endregion

// #region Individual Avatar
export const getIndividualAvatar = (individualId: Guid | null) => (state: RootState) =>
  individualId ? state.individuals.avatars.entries[individualId.value] : undefined;

export const getIndividualAvatarIsLoading = (individualId: Guid | null) => (state: RootState) =>
  individualId ? state.individuals.avatars.loading[individualId.value] : undefined;

export const getIndividualAvatarError = (individualId: Guid | null) => (state: RootState) =>
  individualId ? state.individuals.avatars.error[individualId.value] : undefined;
// #endregion

export type IndividualError = {
  userId: Guid;
  error: SerializedError;
};
